import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { OFFERS, buildWaLink } from "../data/site";
import { useLang, loc } from "../data/i18n";
import { BrandSymbol } from "./BrandLogo";

export default function Offers() {
    const { t, lang } = useLang();
    return (
        <section
            id="offres"
            className="relative bg-cream py-24 md:py-32 paper-grain overflow-hidden"
            data-testid="offers-section"
        >
            <BrandSymbol
                size={180}
                className="absolute top-40 -left-12 opacity-20"
            />

            <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
                <div className="text-center mb-16 md:mb-20">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-coral font-bold mb-5 inline-flex items-center gap-3">
                        <span className="inline-block w-8 h-px bg-coral" />
                        {t.offers.kicker}
                        <span className="inline-block w-8 h-px bg-coral" />
                    </p>
                    <h2 className="font-serif font-normal text-ocean text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-3xl mx-auto">
                        {t.offers.titleA}{" "}
                        <em className="coral-italic">{t.offers.titleB}</em>{" "}
                        {t.offers.titleC}
                    </h2>
                    <p className="mt-6 text-ink/70 max-w-xl mx-auto leading-relaxed">
                        {t.offers.intro}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {OFFERS.map((raw, idx) => {
                        const offer = loc(raw, lang);
                        const featured = offer.featured;
                        return (
                            <motion.div
                                key={offer.id}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, delay: idx * 0.12 }}
                                className={`relative p-8 md:p-10 flex flex-col ${
                                    featured
                                        ? "bg-ocean text-cream md:-mt-6 md:pb-14 border-2 border-coral shadow-[0_20px_60px_rgba(27,108,168,0.25)]"
                                        : "bg-white/70 backdrop-blur-sm border border-ink/10 text-ink"
                                }`}
                                data-testid={`offer-card-${offer.id}`}
                            >
                                {featured && (
                                    <div className="absolute -top-3 left-8 right-8 flex justify-center">
                                        <span className="bg-coral text-cream uppercase tracking-[0.25em] text-[10px] font-bold px-4 py-1.5">
                                            {offer.tag}
                                        </span>
                                    </div>
                                )}

                                {!featured && (
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-coral font-bold mb-6">
                                        {offer.tag}
                                    </p>
                                )}

                                <h3
                                    className={`font-serif ${
                                        featured ? "text-cream" : "text-ocean"
                                    } text-4xl md:text-5xl mb-4 mt-2`}
                                >
                                    {offer.name}
                                </h3>
                                <div
                                    className={`flex items-baseline gap-2 mb-8 pb-8 border-b ${
                                        featured ? "border-cream/20" : "border-ink/15"
                                    }`}
                                >
                                    <span
                                        className={`font-serif text-5xl md:text-6xl ${
                                            featured ? "text-lagoon" : "text-coral"
                                        }`}
                                    >
                                        {offer.price}
                                    </span>
                                    <span
                                        className={`text-xs ${
                                            featured ? "text-cream/60" : "text-ink/60"
                                        }`}
                                    >
                                        {t.offers.perOrder}
                                    </span>
                                </div>

                                <ul className="space-y-4 mb-10 flex-1">
                                    {offer.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className={`flex gap-3 text-sm leading-relaxed ${
                                                featured ? "text-cream/90" : "text-ink/85"
                                            }`}
                                        >
                                            <Check
                                                size={16}
                                                className={
                                                    featured
                                                        ? "text-lagoon flex-shrink-0 mt-1"
                                                        : "text-coral flex-shrink-0 mt-1"
                                                }
                                                strokeWidth={2}
                                            />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>

                                <a
                                    href={buildWaLink(
                                        t.offers.waMessage(offer.name, offer.price),
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`group inline-flex items-center justify-center gap-2 w-full py-4 text-xs uppercase tracking-[0.2em] font-semibold transition-colors border-2 rounded-lg ${
                                        featured
                                            ? "bg-coral border-coral text-cream hover:bg-transparent hover:text-cream"
                                            : "bg-coral border-coral text-cream hover:bg-transparent hover:text-coral"
                                    }`}
                                    data-testid={`offer-cta-${offer.id}`}
                                >
                                    {t.offers.cta}
                                    <span className="group-hover:translate-x-1 transition-transform">
                                        →
                                    </span>
                                </a>
                            </motion.div>
                        );
                    })}
                </div>

                <p className="text-center text-ink/60 text-xs mt-12">
                    {t.offers.disclaimer}
                </p>
            </div>
        </section>
    );
}
